import { motion } from 'framer-motion'
import { ArrowRight, Building2, Lock, Users } from '@digital-twins/design-system/icons'
import { PublisherPill } from '@/components/audiences/PublisherPill'
import Link from '@/routing/Link'
import type { AudienceSummary } from '@/lib/api'
import { cn } from '@/lib/utils'
import { HomeCarouselArrowButton } from './HomeCarouselArrowButton'
import { HomeCarouselCardFrame } from './HomeCarouselCardFrame'
import { HomeCardMetaChip } from './HomeCardMetaChip'
import { wrapIndex, getOffset, getSlotTarget, getZIndex } from './carousel-utils'
import { CoverFlowSkeleton } from './CoverFlowSkeleton'
import { Text } from '@/components/ui/Text'
import { Heading } from '@/components/ui/Heading'
import { Button } from '@/components/ui/button'
import { FALLBACK_DISPLAY_FONT_CLASS } from '@/pages/AudiencesPage/utils/display-fonts'
import { getDisplayFontConfig, fontSizeStyle } from '@/pages/AudiencesPage/utils/font-config'
import {
  resolveAudienceCanonicalCoverUrl,
  resolveAudienceTitleFont,
} from '@/pages/AudiencesPage/utils/audience-presentation'
import { audienceTierDisplayName } from '@/lib/billing/display-names'

interface AudienceCoverFlowProps {
  audiences: AudienceSummary[]
  isLoading: boolean
  activeIndex: number
  onActiveIndexChange: (index: number) => void
}

function AudienceCard({
  audience,
  isActive,
  onClick,
}: {
  audience: AudienceSummary
  isActive: boolean
  onClick: () => void
}) {
  const wizardTagline = (audience.stageMetadata as any)?.tagline?.trim() || null
  const narrativeHeader = audience.narrativeCard?.header?.trim() || null
  const descriptionFallback = audience.aiDescription || audience.description || ''
  const tagline = wizardTagline
    ?? narrativeHeader
    ?? (descriptionFallback
      ? descriptionFallback.split(/\s+/).slice(0, 6).join(' ') + (descriptionFallback.split(/\s+/).length > 6 ? '...' : '')
      : '')

  const categoryParts: Array<string> = []
  if (audience.audienceType) categoryParts.push(audience.audienceType)
  const tierLabel = audience.tier ? audienceTierDisplayName(audience.tier) : 'Standard'
  categoryParts.push(tierLabel)
  const categoryText = categoryParts.join(' · ')

  const resolvedCoverImage = resolveAudienceCanonicalCoverUrl(audience)
  const resolvedTitleFont = resolveAudienceTitleFont(audience)
  const titleFontFamily = resolvedTitleFont
    ? `'${resolvedTitleFont}', sans-serif`
    : undefined
  const fontConfig = getDisplayFontConfig(resolvedTitleFont)

  const accessType = audience.sharingMode === 'marketplace' || (audience.canEdit === false) ? 'shared' : 'private'

  const isMarketplace = audience.sharingMode === 'marketplace'

  const topLeft = isMarketplace ? (
    <PublisherPill icon={audience.logoUrl ?? null} name="" iconOnly />
  ) : accessType === 'private' ? (
    <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/15 border border-primary/20">
      <Lock className="h-3 w-3 text-primary" />
    </div>
  ) : (
    <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary border border-primary">
      <Users className="h-3 w-3 text-primary-foreground" />
    </div>
  )

  return (
    <HomeCarouselCardFrame
      imageUrl={resolvedCoverImage}
      isActive={isActive}
      onClick={onClick}
      ariaLabel={audience.name}
      topLeft={topLeft}
      bottomContent={
        <div className="flex flex-col justify-end text-white">
          <h3
            className={cn(
              titleFontFamily ? undefined : FALLBACK_DISPLAY_FONT_CLASS,
              'text-white tracking-tight line-clamp-2',
            )}
            style={fontSizeStyle(fontConfig.coverFlow, titleFontFamily)}
          >
            {audience.name}
          </h3>
          {tagline && (
            <p className="text-[14px] font-medium font-sans text-white/90 mt-1.5 line-clamp-1">
              {tagline}
            </p>
          )}
          <p className="text-[14px] font-normal text-white/85 mt-0.5">
            {categoryText}
          </p>
        </div>
      }
    />
  )
}

const PLACEHOLDER_SLOTS = [
  { x: -244, y: 0, scale: 0.884, rotate: -4, zIndex: 10 },
  { x: 0, y: -36, scale: 1, rotate: 0, zIndex: 20 },
  { x: 244, y: 0, scale: 0.884, rotate: 4, zIndex: 10 },
]

function AudienceCoverFlowEmpty() {
  return (
    <div className="flex w-full justify-center px-2 pb-2 pt-10">
      <div className="relative flex w-[700px] items-center justify-center">
        <div className="relative h-[340px] w-full pointer-events-none">
          {PLACEHOLDER_SLOTS.map((slot, i) => (
            <div
              key={i}
              className="absolute bottom-3 left-1/2"
              style={{
                marginLeft: -138,
                zIndex: slot.zIndex,
                transform: `translateX(${slot.x}px) translateY(${slot.y}px) scale(${slot.scale}) rotate(${slot.rotate}deg)`,
              }}
            >
              <div
                className="relative h-[300px] w-[276px] rounded-lg"
                style={{
                  background: 'var(--home-card-placeholder-gradient)',
                  boxShadow: 'var(--home-card-shadow)',
                }}
              />
            </div>
          ))}
        </div>

        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 text-center pb-20">
          <Heading level={4} className="font-sans text-[18px] font-medium text-foreground">
            No audiences available yet
          </Heading>
          <Button variant="default" size="default" asChild className="rounded-[6px] bg-primary text-base text-primary-foreground hover:bg-primary/90">
            <Link href="/audiences">
              Browse audiences
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

export function AudienceCoverFlow({
  audiences,
  isLoading,
  activeIndex,
  onActiveIndexChange,
}: AudienceCoverFlowProps) {
  const goLeft = () => onActiveIndexChange(wrapIndex(activeIndex - 1, audiences.length))
  const goRight = () => onActiveIndexChange(wrapIndex(activeIndex + 1, audiences.length))

  if (isLoading) {
    return <CoverFlowSkeleton />
  }

  if (audiences.length === 0) {
    return <AudienceCoverFlowEmpty />
  }

  return (
    <div className="flex w-full justify-center px-2 pb-2 pt-10">
      <div className="relative flex w-[700px] items-center justify-center">
        {audiences.length > 1 ? (
          <HomeCarouselArrowButton
            direction="left"
            onClick={goLeft}
            ariaLabel="Previous audience"
            className="absolute left-0 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2"
          />
        ) : null}

        {/* CoverFlow stage: fixed height, cards slide via absolute positioning */}
        <div className="relative h-[300px] w-full">
          {audiences.map((audience, idx) => {
            const offset = getOffset(idx, activeIndex, audiences.length)
            const target = getSlotTarget(offset)
            const isVisible = Math.abs(offset) <= 1

            return (
              <motion.div
                key={audience.id}
                initial={false}
                className="absolute bottom-0 left-1/2"
                style={{
                  marginLeft: -138,
                  zIndex: getZIndex(offset),
                  pointerEvents: isVisible ? 'auto' : 'none',
                }}
                animate={target}
                transition={{
                  type: 'spring',
                  stiffness: 280,
                  damping: 28,
                  mass: 0.9,
                }}
              >
                <AudienceCard
                  audience={audience}
                  isActive={offset === 0}
                  onClick={() => onActiveIndexChange(idx)}
                />
              </motion.div>
            )
          })}
        </div>

        {audiences.length > 1 ? (
          <HomeCarouselArrowButton
            direction="right"
            onClick={goRight}
            ariaLabel="Next audience"
            className="absolute right-0 top-1/2 z-30 translate-x-1/2 -translate-y-1/2"
          />
        ) : null}
      </div>
    </div>
  )
}
